
import { Component, Prop, Vue } from 'vue-property-decorator'
import moment from 'moment'

type PersonOption = { id: number | string, label: string, full_name?: string, name?: string }
type TextRow = Record<string, string>
type SelectOptionNode = { componentOptions?: { children?: Array<{ text?: string }> } }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormAnswers = Record<string, any>

@Component
export default class KAM005V1 extends Vue {
  @Prop({ required: true }) readonly answers!: FormAnswers
  @Prop({ default: () => [] }) readonly projectLeaders!: PersonOption[]
  @Prop({ default: () => [] }) readonly siteManagers!: PersonOption[]
  @Prop({ default: () => [] }) readonly qualityManagement!: PersonOption[]
  @Prop({ default: () => [] }) readonly employees!: PersonOption[]
  @Prop({ default: () => [] }) readonly constructionWorkers!: PersonOption[]
  @Prop({ default: () => ({}) }) readonly media!: Record<string, unknown>

  raciRoles = [
    { key: 'kam', label: 'KAM', title: 'Kwaliteit, Arbo & Milieu' },
    { key: 'ow', label: 'OW', title: 'Ontwerp / Werkvoorbereiding' },
    { key: 'pv', label: 'PV', title: 'Planvoorbereiding' },
    { key: 'pl', label: 'PL', title: 'Projectleider' },
    { key: 'wv', label: 'WV', title: 'Werkvoorbereider' },
    { key: 'uit', label: 'UIT', title: 'Uitvoerder' },
    { key: 'oa', label: 'OA', title: 'Onderaannemer' }
  ]

  wasteOptions: Record<string, string> = {
    puin: 'Puin',
    dakbedekking: 'Dakbedekking',
    papierKarton: 'Papier en karton',
    overigPuin: 'Overig puin (en zand)',
    kca: 'Klein chemisch afval (KCA)',
    metalen: 'Metalen',
    schoonHout: 'Schoon hout',
    chemischAfval: 'Overig chemisch afval',
    bouwSloopafval: 'Overig ongesorteerd bouwafval',
    geverfdHout: 'Overig (geverfd) hout',
    gips: 'Gips',
    ldpe: 'LDPE folie'
  }

  evaluationQuestions = [
    { key: 'risksFollowed', label: 'Welke risico\'s zijn op tijd geïnventariseerd en op de juiste manier opgevolgd?' },
    { key: 'improvements', label: 'Op welke punten zijn verbeteringen mogelijk qua risico-inschatting en -opvolging?' },
    { key: 'planningQualityVgm', label: 'Hoe was de verhouding planning - kwaliteit - VGM?' },
    { key: 'companyAttention', label: 'Zijn er aspecten die ook bedrijfsbreed aandacht moeten hebben?' },
    { key: 'dossierReady', label: 'Zijn de overall projectevaluaties en het VGM-dossier (onderhoudsfase) opgesteld?' }
  ]

  get formAnswers (): FormAnswers {
    return this.answers || {}
  }

  get parties (): TextRow[] {
    return Array.isArray(this.formAnswers.parties) ? this.formAnswers.parties : []
  }

  get projectTeam (): TextRow[] {
    return Array.isArray(this.formAnswers.projectTeam) ? this.formAnswers.projectTeam : []
  }

  get coordinationTasks (): TextRow[] {
    return Array.isArray(this.formAnswers.coordinationTasks) ? this.formAnswers.coordinationTasks : []
  }

  get initiationRisks (): TextRow[] {
    return Array.isArray(this.formAnswers.initiationRisks) ? this.formAnswers.initiationRisks : []
  }

  get designRisks (): TextRow[] {
    return Array.isArray(this.formAnswers.designRisks) ? this.formAnswers.designRisks : []
  }

  get executionParties (): TextRow[] {
    return Array.isArray(this.formAnswers.executionParties) ? this.formAnswers.executionParties : []
  }

  get meetings (): TextRow[] {
    return Array.isArray(this.formAnswers.meetings) ? this.formAnswers.meetings : []
  }

  get collectiveRisks (): TextRow[] {
    return Array.isArray(this.formAnswers.collectiveRisks) ? this.formAnswers.collectiveRisks : []
  }

  get bhvResources (): TextRow[] {
    return Array.isArray(this.formAnswers.bhvResources) ? this.formAnswers.bhvResources : []
  }

  get activeWasteLabels (): string[] {
    const waste: Record<string, boolean> = this.formAnswers.wasteStreams || {}
    return Object.keys(waste)
      .filter(key => waste[key] === true && Boolean(this.wasteOptions[key]))
      .map(key => this.wasteOptions[key])
  }

  getEvaluationAnswer (key: string): string {
    const evalMap: Record<string, string> = this.formAnswers.evaluation || {}
    return evalMap[key] || '-'
  }

  hasText (val: unknown): boolean {
    return typeof val === 'string' && val.trim().length > 0
  }

  cleanSubject (subject: string | undefined): string {
    return (subject || '').replace(/\s*\(aanvulling\)/i, '').trim().toLowerCase()
  }

  findMatchingInitiationRisk (subject: string | undefined): TextRow | null {
    if (this.formAnswers.externalClient) return null
    const clean = this.cleanSubject(subject)
    if (!clean) return null
    return this.initiationRisks.find(r => this.cleanSubject(r.subject) === clean) || null
  }

  isSharedWithInitiation (subject: string | undefined): boolean {
    return !!this.findMatchingInitiationRisk(subject)
  }

  getInitiationDescription (subject: string | undefined): string {
    const match = this.findMatchingInitiationRisk(subject)
    return match?.description || ''
  }

  formatSignoffDate (dateVal: unknown): string {
    if (!dateVal) return ''
    const m = moment(dateVal as moment.MomentInput)
    return m.isValid() ? m.format('DD-MM-YYYY') : String(dateVal)
  }

  // Voortgangsberekeningen per fase
  get section1Progress (): number {
    let filled = 0
    if (this.hasText(this.formAnswers.buildMethod)) filled++
    if (this.hasText(this.formAnswers.designPhasePeriod)) filled++
    if (this.hasText(this.formAnswers.executionPhasePeriod)) filled++
    if (this.hasText(this.formAnswers.projectDescription)) filled++
    return Math.round((filled / 4) * 100)
  }

  get section2Progress (): number {
    if (this.formAnswers.externalClient) return 100
    let filled = 0
    if (this.hasText(this.formAnswers.initiationPerformedWith)) filled += 1
    if (this.hasText(this.formAnswers.initiationDate) || this.formAnswers.initiationDate) filled += 1
    const risks = this.initiationRisks
    if (risks.length > 0) {
      const risksFilled = risks.filter(r => this.hasText(r.description)).length
      const risksScore = Math.min(2, (risksFilled / risks.length) * 2)
      filled += risksScore
    } else {
      filled += 2
    }
    return Math.round((filled / 4) * 100)
  }

  get section3Progress (): number {
    let filled = 0
    if (this.hasText(this.formAnswers.designPerformedWith)) filled += 1
    if (this.hasText(this.formAnswers.designDate) || this.formAnswers.designDate) filled += 1
    const risks = this.designRisks
    if (risks.length > 0) {
      const risksFilled = risks.filter(r => {
        if (this.isSharedWithInitiation(r.subject)) {
          return this.hasText(r.results)
        }
        return this.hasText(r.description) || this.hasText(r.results)
      }).length
      const risksScore = Math.min(2, (risksFilled / risks.length) * 2)
      filled += risksScore
    } else {
      filled += 2
    }
    return Math.round((filled / 4) * 100)
  }

  get section4Progress (): number {
    let filled = 0
    if (this.hasText(this.formAnswers.collectiveRisksPerformedWith)) filled += 1
    if (this.hasText(this.formAnswers.collectiveRisksDate) || this.formAnswers.collectiveRisksDate) filled += 1
    const bhv = this.bhvResources
    if (bhv.length > 0 && bhv.every(r => this.hasText(r.location))) filled += 1
    const waste: Record<string, boolean> = this.formAnswers.wasteStreams || {}
    if (Object.keys(waste).some(k => waste[k] === true)) filled += 1
    return Math.round((filled / 4) * 100)
  }

  get section5Progress (): number {
    let filled = 0
    if (this.hasText(this.formAnswers.evaluationPerformedWith)) filled += 1
    if (this.hasText(this.formAnswers.evaluationDate) || this.formAnswers.evaluationDate) filled += 1
    const evalMap: Record<string, string> = this.formAnswers.evaluation || {}
    let questionsFilled = 0
    this.evaluationQuestions.forEach(q => {
      if (this.hasText(evalMap[q.key])) questionsFilled++
    })
    const evalScore = (questionsFilled / 5) * 2
    filled += evalScore
    return Math.round((filled / 4) * 100)
  }

  get phaseList (): Array<{ key: string, title: string, progress: number, approvalKey: string }> {
    if (this.formAnswers.externalClient) {
      return [
        { key: 'plan', title: 'Planvoorbereiding', progress: this.section1Progress, approvalKey: 'phasePlanApproved' },
        { key: 'design', title: 'Ontwerpfase', progress: this.section3Progress, approvalKey: 'phaseDesignApproved' },
        { key: 'execution', title: 'Uitvoeringsfase', progress: this.section4Progress, approvalKey: 'phaseExecutionApproved' },
        { key: 'evaluation', title: 'Evaluatiefase', progress: this.section5Progress, approvalKey: 'phaseEvaluationApproved' }
      ]
    }
    return [
      { key: 'plan', title: 'Planvoorbereiding', progress: this.section1Progress, approvalKey: 'phasePlanApproved' },
      { key: 'initiation', title: 'Initiatieffase', progress: this.section2Progress, approvalKey: 'phaseInitiationApproved' },
      { key: 'design', title: 'Ontwerpfase', progress: this.section3Progress, approvalKey: 'phaseDesignApproved' },
      { key: 'execution', title: 'Uitvoeringsfase', progress: this.section4Progress, approvalKey: 'phaseExecutionApproved' },
      { key: 'evaluation', title: 'Evaluatiefase', progress: this.section5Progress, approvalKey: 'phaseEvaluationApproved' }
    ]
  }

  // Ondertekening en Betrokkenen
  normalizePeople (people: PersonOption[]): PersonOption[] {
    return people.map(person => ({
      ...person,
      label: person.label || person.full_name || person.name || String(person.id)
    }))
  }

  filterPeople (input: string, option: SelectOptionNode): boolean {
    const label = String(option?.componentOptions?.children?.map(child => child.text || '').join('') || '').toLowerCase()
    return label.includes(input.toLowerCase())
  }

  get currentProject (): { project_leader?: { id: number | string, name?: string }, site_manager?: { id: number | string, name?: string } } | null {
    return this.$store.getters.currentKAMForm?.project || null
  }

  get projectLeaderOptions (): PersonOption[] {
    const list = this.normalizePeople(this.projectLeaders)
    const currentLeader = this.currentProject?.project_leader
    if (currentLeader && !list.some(p => String(p.id) === String(currentLeader.id))) {
      list.unshift({ id: currentLeader.id, label: currentLeader.name || String(currentLeader.id) })
    }
    const storedName = typeof this.formAnswers.projectLeaderName === 'string' ? this.formAnswers.projectLeaderName : ''
    const storedId = (typeof this.formAnswers.projectLeaderId === 'string' || typeof this.formAnswers.projectLeaderId === 'number') ? this.formAnswers.projectLeaderId : null
    if (storedName && !list.some(p => p.label === storedName || (storedId != null && String(p.id) === String(storedId)))) {
      list.push({ id: storedId ?? storedName, label: storedName })
    }
    return list
  }

  get selectedProjectLeader (): number | string | null {
    if (typeof this.formAnswers.projectLeaderId === 'number' || typeof this.formAnswers.projectLeaderId === 'string') {
      return this.formAnswers.projectLeaderId
    }
    if (typeof this.formAnswers.projectLeaderName === 'string' && this.formAnswers.projectLeaderName) {
      const match = this.projectLeaderOptions.find(p => p.label === this.formAnswers.projectLeaderName)
      if (match) return match.id
    }
    return null
  }

  onProjectLeaderChange (value: number | string | undefined): void {
    const match = this.projectLeaderOptions.find(p => String(p.id) === String(value))
    this.$set(this.formAnswers, 'projectLeaderId', match?.id ?? (value || null))
    this.$set(this.formAnswers, 'projectLeaderName', match?.label ?? (value ? String(value) : ''))
  }

  get siteManagerOptions (): PersonOption[] {
    const list = this.normalizePeople(this.siteManagers)
    const currentManager = this.currentProject?.site_manager
    if (currentManager && !list.some(p => String(p.id) === String(currentManager.id))) {
      list.unshift({ id: currentManager.id, label: currentManager.name || String(currentManager.id) })
    }
    const storedName = typeof this.formAnswers.siteManagerName === 'string' ? this.formAnswers.siteManagerName : ''
    const storedId = (typeof this.formAnswers.siteManagerId === 'string' || typeof this.formAnswers.siteManagerId === 'number') ? this.formAnswers.siteManagerId : null
    if (storedName && !list.some(p => p.label === storedName || (storedId != null && String(p.id) === String(storedId)))) {
      list.push({ id: storedId ?? storedName, label: storedName })
    }
    return list
  }

  get selectedSiteManager (): number | string | null {
    if (typeof this.formAnswers.siteManagerId === 'number' || typeof this.formAnswers.siteManagerId === 'string') {
      return this.formAnswers.siteManagerId
    }
    if (typeof this.formAnswers.siteManagerName === 'string' && this.formAnswers.siteManagerName) {
      const match = this.siteManagerOptions.find(p => p.label === this.formAnswers.siteManagerName)
      if (match) return match.id
    }
    return null
  }

  onSiteManagerChange (value: number | string | undefined): void {
    const match = this.siteManagerOptions.find(p => String(p.id) === String(value))
    this.$set(this.formAnswers, 'siteManagerId', match?.id ?? (value || null))
    this.$set(this.formAnswers, 'siteManagerName', match?.label ?? (value ? String(value) : ''))
  }

  get qualityManagementOptions (): PersonOption[] {
    const list = this.normalizePeople(this.qualityManagement)
    if (!list.some(p => /henk/i.test(p.label))) {
      list.unshift({ id: 'henk-tijssen', label: 'Henk Tijssen' })
    }
    const storedName = typeof this.formAnswers.kamManagerName === 'string' ? this.formAnswers.kamManagerName : ''
    const storedId = (typeof this.formAnswers.kamManagerId === 'string' || typeof this.formAnswers.kamManagerId === 'number') ? this.formAnswers.kamManagerId : null
    if (storedName && !list.some(p => p.label === storedName || (storedId != null && String(p.id) === String(storedId)))) {
      list.push({ id: storedId ?? storedName, label: storedName })
    }
    return list
  }

  get selectedKamManager (): number | string | null {
    if (typeof this.formAnswers.kamManagerId === 'number' || typeof this.formAnswers.kamManagerId === 'string') {
      return this.formAnswers.kamManagerId
    }
    if (typeof this.formAnswers.kamManagerName === 'string' && this.formAnswers.kamManagerName) {
      const match = this.qualityManagementOptions.find(p => p.label === this.formAnswers.kamManagerName)
      if (match) return match.id
    }
    return null
  }

  onKamManagerChange (value: number | string | undefined): void {
    const match = this.qualityManagementOptions.find(p => String(p.id) === String(value))
    this.$set(this.formAnswers, 'kamManagerId', match?.id ?? (value || null))
    this.$set(this.formAnswers, 'kamManagerName', match?.label ?? (value ? String(value) : ''))
  }
}
